import { css, styled } from '../../../common.styles';
import { baseColor, mobile, primaryFont, tablet } from '../../../styles/general/theme';
import React from 'react';
import { ButtonProps } from '../general/common-button';

const BaseStyle = css<{ fullWidth?: boolean; width?: string }>`
  font-family: ${primaryFont};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  text-transform: uppercase;
  padding: 12px 24px;
  cursor: pointer;
  user-select: none;
  width: fit-content;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  text-decoration: none;

  ${(p) =>
    p.fullWidth &&
    css`
      width: 100%;
      justify-content: center;
    `};

  ${(p) =>
    p.width &&
    css`
      width: ${p.width};
    `};

  @media (max-width: ${tablet}px) {
    justify-content: center;
  }

  @media (max-width: ${mobile}px) {
    justify-content: center;
    width: ${(p) => (p.fullWidth ? '100%' : 'fit-content')};
  }
`;

const getColor = (darkBackground: boolean | undefined, lightColor: number, darkColor: number) =>
  baseColor(darkBackground ? darkColor : lightColor);

export const primaryStyle = css<{ darkBackground?: boolean; disabled?: boolean; darkMode?: boolean }>`
  color: ${(p) => getColor(p.darkBackground, 900, 110)};
  background-color: ${(p) => getColor(p.darkBackground, 110, 900)};

  &:hover {
    background-color: ${(p) => getColor(p.darkBackground, 120, 200)};
  }
  ${(p) => p.disabled && disabledPrimaryStyle};

  ${(p) =>
    p.darkMode &&
    css`
      background-color: ${baseColor(900)};
      border-color: ${baseColor(900)};
      color: ${baseColor(110)};

      :hover {
        background-color: ${baseColor(200)};
        border-color: ${baseColor(200)};
        color: ${baseColor(110)};
      }
    `};
`;

export const secondaryStyle = css<{ darkBackground?: boolean; disabled?: boolean; darkMode?: boolean }>`
  border: 1px solid ${(p) => getColor(p.darkBackground, 110, 900)};
  color: ${(p) => getColor(p.darkBackground, 110, 900)};
  background-color: transparent;

  &:hover {
    color: ${(p) => getColor(p.darkBackground, 120, 170)};
    border-color: ${(p) => getColor(p.darkBackground, 120, 170)};
    background-color: transparent;
  }

  ${(p) => p.disabled && disabledSecondaryStyle};

  ${(p) =>
    p.darkMode &&
    css`
      background-color: transparent;
      border-color: ${baseColor(900)};
      color: ${baseColor(900)};

      :hover {
        background-color: transparent;
        border-color: ${baseColor(170)};
        color: ${baseColor(170)};
      }
    `};
`;

const disabledPrimaryStyle = css<{ darkBackground?: boolean }>`
  cursor: not-allowed;
  border-color: ${baseColor(190)};
  color: ${baseColor(185)};
  background-color: ${baseColor(190)};
  box-shadow: none;
  &:hover {
    color: ${baseColor(185)};
    background-color: ${baseColor(190)};
    border-color: ${baseColor(190)};
    box-shadow: none;
  }
`;

const disabledSecondaryStyle = css<{ darkBackground?: boolean }>`
  cursor: not-allowed;
  ${(p) => {
    const color = getColor(p.darkBackground, 190, 180);
    return css`
      color: ${color};
      background-color: transparent;
      box-shadow: none;
      border: 1px solid ${color};
      &:hover {
        color: ${color};
        background-color: transparent;
        border: 1px solid ${color};
        box-shadow: none;
      }
    `;
  }}
`;

export const LanciaButtonStyle = css<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  variant?: string;
  darkMode?: boolean;
}>`
  ${BaseStyle};

  ${(p) => (p.variant?.toLowerCase() === 'primary' || !p.variant) && primaryStyle};
  ${(p) => p.variant?.toLowerCase() === 'secondary' && secondaryStyle};

  @media (max-width: ${mobile}px) {
    align-self: center;
    width: 100%;
  }
`;
export const LanciaMainButton = styled.a<ButtonProps>`
  ${LanciaButtonStyle};
`;

export const LanciaPrimaryButton = css<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  variant?: string;
}>`
  ${BaseStyle};
  ${primaryStyle};
  ${disabledPrimaryStyle};
`;

export const LanciaSecondaryButton = css<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  variant?: string;
}>`
  ${BaseStyle};
  ${secondaryStyle};
  ${disabledSecondaryStyle};
`;

export const LanciaChevronLeftArrowIcon = ({ isDark }: { isDark?: boolean }) => (
  <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M19.32 25.5862L20.141 24.7652L11.781 16.4152L20.041 8.14522L19.22 7.32422L10.139 16.4152L19.32 25.5862Z"
      fill={isDark ? '#222222' : '#FFFFFF'}
    />
  </svg>
);

export const LanciaChevronRightArrowIcon = ({ isDark }: { isDark?: boolean }) => (
  <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.7 7.32422L11.9 8.12422L20.3 16.5242L12 24.7242L12.8 25.5242L21.9 16.4242L12.7 7.32422Z"
      fill={isDark ? '#222222' : '#FFFFFF'}
    />
  </svg>
);

const LanciaArrowAndTextButtonWrapper = styled.a<ButtonProps>`
  font-family: ${primaryFont};
  font-size: 12px;
  letter-spacing: 1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${baseColor(110)};

  &:hover {
    color: ${baseColor(180)};
  }

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${baseColor(190)};

      svg path {
        fill: ${baseColor(190)};
      }

      &:hover {
        color: ${baseColor(190)};
      }
    `};
`;

const LanciaTextButtonWrapper = styled.a<ButtonProps>`
  ${BaseStyle};
  padding: 0;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 14px;
  text-transform: uppercase;
  color: ${(p) => getColor(p?.darkBackground, 110, 900)};
  text-decoration: underline;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    text-decoration-line: none;
    color: ${(p) => getColor(p?.darkBackground, 180, 800)};
  }

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${getColor(p.darkBackground, 200, 180)};
      text-decoration: none;
      &:hover {
        color: ${getColor(p.darkBackground, 200, 180)};
      }
    `};

  ${(p) =>
    p.darkMode &&
    css`
      color: ${baseColor(900)};
      text-decoration: underline;

      :hover {
        text-decoration: none;
        color: ${baseColor(900)};
      }
    `};
`;

export const LanciaArrowAndTextButton: React.FC<ButtonProps> = (props) => {
  const position = props.iconPosition || 'left';
  return (
    <LanciaArrowAndTextButtonWrapper {...props}>
      {position === 'left' && <LanciaChevronLeftArrowIcon isDark={!!props?.darkBackground} />}
      {props.children}
      {position === 'right' && <LanciaChevronRightArrowIcon isDark={!!props?.darkBackground} />}
    </LanciaArrowAndTextButtonWrapper>
  );
};

export const LanciaButton: React.FC<ButtonProps> = (props: ButtonProps) => {
  switch (props?.variant?.toLowerCase()) {
    case 'primary':
      return <LanciaMainButton {...props} />;
    case 'secondary':
      return <LanciaMainButton {...props} />;
    case 'tertiary':
      return <LanciaTextButtonWrapper {...props} />;
    case 'icon':
      return <LanciaArrowAndTextButton {...props} />;
    default:
      return null;
  }
};
